import React from "react";
import { useTranslator } from "../hooks/useTranslator";
import { Button, Spinner, Modal, Badge } from "react-bootstrap";
import SearchableSelect from "./SearchableSelect";
import ToastError from "../elements/ToastError";

/**
 * Reusable translator component using Google Cloud API
 * 
 * @param {Object} props - Component props
 * @param {string} props.text - Text to translate
 * @param {function} props.onTranslated - Callback when translation is applied
 * @param {boolean} props.showWordCount - Whether to show word count (default: true)
 * @param {string} props.buttonSize - Size of translate button (default: "sm")
 * @param {string} props.buttonVariant - Variant of translate button (default: "outline-primary")
 * @param {string} props.modalTitle - Title for translation modal (default: "Translation Preview")
 * @param {React.ReactNode} props.children - Optional content to display below language selector
 * @returns {JSX.Element} Translator component
 */
const Translator = ({
  text,
  onTranslated,
  showWordCount = true,
  buttonSize = "sm",
  buttonVariant = "outline-primary",
  modalTitle = "Translation Preview",
  children,
}) => {
  const {
    availableLanguages,
    selectedLanguage,
    translatedText,
    showTranslationModal,
    isTranslating,
    error,
    setSelectedLanguage,
    handleTranslate,
    closeModal,
    clearError,
  } = useTranslator();

  // Apply the translation when confirmed
  const applyTranslation = () => {
    if (onTranslated && translatedText) {
      onTranslated(translatedText);
    }
    closeModal();
  };

  // Copy translation to clipboard
  const copyToClipboard = () => {
    if (translatedText) {
      navigator.clipboard.writeText(translatedText);
    }
  };

  // Calculate word count if needed
  const wordCount = showWordCount && text ? text.trim().split(/\s+/).filter(Boolean).length : null;

  return (
    <>
      <ToastError show={!!error} message={error} onClose={clearError} />
      
      <div className="d-flex align-items-center">
        <div className="me-2">
          <SearchableSelect
            options={availableLanguages}
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            size={buttonSize}
            placeholder="Search languages..."
          />
        </div>
        
        <Button
          size={buttonSize}
          variant={buttonVariant}
          onClick={() => handleTranslate(text)}
          disabled={!selectedLanguage || !text || isTranslating}
        >
          {isTranslating ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-1"
              />
              Translating...
            </>
          ) : (
            "Translate"
          )}
        </Button>
      </div>
      
      {/* Optional children content */}
      {children}
      
      {/* Word count display */}
      {showWordCount && (
        <div className="d-flex justify-content-between mt-1">
          <div>
            Word Count: {wordCount || 0}
          </div>
          {selectedLanguage && (
            <Badge bg="info">Target: {selectedLanguage}</Badge>
          )}
        </div>
      )}
      
      {/* Translation Preview Modal */}
      <Modal show={showTranslationModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h6>Original Text:</h6>
            <div className="p-2 bg-light border rounded">
              {text}
            </div>
          </div>
          <div className="mb-3">
            <h6>Translated Text ({selectedLanguage}):</h6>
            <div className="p-2 bg-light border rounded">
              {translatedText}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button 
            variant="primary"
            onClick={applyTranslation}
          >
            Apply Translation
          </Button>
          <Button
            variant="outline-secondary"
            onClick={copyToClipboard}
          >
            Copy to Clipboard
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Translator;
